import { Injectable } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  AbstractControl,
  Validators,
  ValidationErrors,
} from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { IAssignmentFormData } from 'src/app/interfaces/self-serve/assignment-manager/iassignment-form';
import { IAssignmentDetails } from 'src/app/interfaces/self-serve/assignment-manager/responses/iassignment-details';
import { toDateInputString } from 'src/app/utils/date-parser.util';

// Date validation constants
export const DATE_VALIDATION = {
  MIN_DAYS_AFTER_START: 1,
  MILLISECONDS_PER_DAY: 24 * 60 * 60 * 1000,
  ERROR_KEY: 'invalidDateRange',
} as const;

@Injectable({
  providedIn: 'root',
})
export class AssignmentReactiveFormService {
  private assignments = new Map<string, BehaviorSubject<FormGroup>>();
  private updatedAtMap = new Map<string, BehaviorSubject<string>>();
  private assignmentStatuses = new Map<string, string>();
  private readonly editableFieldStatuses = new Set(['STAGED', 'DRAFT']);

  private assignmentNameConflictSubject = new BehaviorSubject<boolean>(false);
  assignmentNameConflict = this.assignmentNameConflictSubject.asObservable();

  private readonly possibleStatuses = {
    COMPLETE: 'COMPLETE',
    ARCHIVED: 'ARCHIVED',
    ACTIVE: 'ACTIVE',
    DRAFT: 'DRAFT',
    STAGED: 'STAGED',
    TESTING: 'TESTING',
    CLOSED: 'CLOSED',
  };

  constructor(private fb: FormBuilder) {}

  setAssignmentStatus(uuid: string, status: string): void {
    this.assignmentStatuses.set(uuid, status);
  }

  isReadOnly(uuid: string): boolean {
    const status = this.assignmentStatuses.get(uuid);
    return !this.editableFieldStatuses.has(status ?? '');
  }

  isCompleted(uuid: string): boolean {
    const status = this.assignmentStatuses.get(uuid);
    return status === this.possibleStatuses.COMPLETE || status === this.possibleStatuses.CLOSED;
  }

  isClosedOrArchived(uuid: string): boolean {
    const status = this.assignmentStatuses.get(uuid);
    return status === this.possibleStatuses.CLOSED || status === this.possibleStatuses.ARCHIVED;
  }

  setReadOnlyMode(uuid: string, field: AbstractControl | null | undefined) {
    const readOnly = this.isReadOnly(uuid);
    if (readOnly) {
      field?.disable();
    } else {
      field?.enable();
    }
  }

  setAssignmentNameConflict(status: boolean): void {
    this.assignmentNameConflictSubject.next(status);
  }

  createAssignment(initialData = {}, uuid: string): string {
    const form = this.createAssignmentForm(initialData);
    this.assignments.set(uuid, new BehaviorSubject(form));

    // Save initial state to localStorage
    this.saveToLocalStorage(uuid, form);

    return uuid;
  }

  initializeAssignmentForm(uuid: string, apiData: any): FormGroup {
    const transformed = this.transformApiData(apiData);
    const form = this.createAssignmentForm(transformed);
    this.setAssignmentStatus(uuid, apiData.status || 'DRAFT');

    if (this.assignments.has(uuid)) {
      this.assignments.get(uuid)!.next(form);
    } else {
      this.assignments.set(uuid, new BehaviorSubject<FormGroup>(form));
    }
    return form;
  }

  getForm(uuid: string): Observable<FormGroup> {
    if (!this.assignments.has(uuid)) {
      throw new Error(`Form with UUID ${uuid} has not been initialized yet.`);
    }
    return this.assignments.get(uuid)!.asObservable();
  }

  patchForm(uuid: string, section: string, data: any): void {
    const subject = this.assignments.get(uuid);
    if (!subject) return;

    const currentForm = subject.value;
    console.log('current assignment form:', currentForm);

    const sectionGroup = currentForm.get(section) as FormGroup;

    if (sectionGroup) {
      sectionGroup.patchValue(data, { onlySelf: true, emitEvent: true });

      // Save updated form to localStorage
      this.saveToLocalStorage(uuid, currentForm);
    }
  }

  deleteAssignment(uuid: string): void {
    this.assignments.delete(uuid);
    localStorage.removeItem(`assignment-${uuid}`);
  }

  getUpdatedAt$(uuid: string): Observable<string> {
    if (!this.updatedAtMap.has(uuid)) {
      const stored = this.loadFromLocalStorage(uuid);
      const timestamp = stored?.updatedAt ?? new Date().toISOString();
      this.updatedAtMap.set(uuid, new BehaviorSubject<string>(timestamp));
    }

    return this.updatedAtMap.get(uuid)!.asObservable();
  }

  private createAssignmentForm(
    data: Partial<IAssignmentFormData> = {}
  ): FormGroup {
    const form = this.fb.group({
      details: this.fb.group({
        moduleAssignmentName: [
          data.details?.moduleAssignmentName || '',
          Validators.required,
        ],
        moduleUuid: [data.details?.moduleUuid || ''],
        startDate: [data.details?.startDate || '', Validators.required],
        endDate: [data.details?.endDate || ''],
        scoreCriteria: [data.details?.scoreCriteria || 0],
        description: [data.details?.description || ''],
        moduleAssignmentType: [
          data.details?.moduleAssignmentType || 'PRACTICE',
          Validators.required,
        ],
        pauseEnabled: [data.details?.pauseEnabled ?? true],
        hideResultsForLearners: [data.details?.hideResultsForLearners ?? false],
        showMissedFacts: [data.details?.showMissedFacts || false],
        simulationsCount: [data.details?.simulationsCount || 0],
      }),
    });

    return form;
  }

  private saveToLocalStorage(uuid: string, form: FormGroup): void {
    try {
      const raw = form.getRawValue();
      const updatedAt = new Date().toISOString();
      const dataToSave = {
        updatedAt,
        ...raw,
      };
      localStorage.setItem(`assignment-${uuid}`, JSON.stringify(dataToSave));

      // emit new updatedAt
      if (!this.updatedAtMap.has(uuid)) {
        this.updatedAtMap.set(uuid, new BehaviorSubject<string>(updatedAt));
      } else {
        this.updatedAtMap.get(uuid)!.next(updatedAt);
      }
    } catch (err) {
      console.error('Failed to save assignment form to localStorage:', err);
    }
  }

  private loadFromLocalStorage(uuid: string): any | null {
    try {
      const raw = localStorage.getItem(`assignment-${uuid}`);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.error('Failed to load assignment form from localStorage:', err);
      return null;
    }
  }

  transformApiData(apiData: IAssignmentDetails): IAssignmentFormData {
    return {
      details: {
        moduleAssignmentName: apiData.name || '',
        moduleUuid: apiData.moduleUuid || '',
        startDate: toDateInputString(apiData.startDate) || '',
        endDate: toDateInputString(apiData.endDate) || '',
        scoreCriteria: apiData.scoreCriteria || 0,
        description: apiData.description || '',
        moduleAssignmentType: apiData.moduleAssignmentType || 'PRACTICE',
        pauseEnabled: apiData.pauseEnabled ?? false,
        hideResultsForLearners: apiData.hideResultsForLearners ?? false,
        showMissedFacts: apiData.showMissedFacts || false,
      },
    };
  }

  static dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const startDate = control.parent?.get('startDate')?.value;
    const endDate = control.parent?.get('endDate')?.value;

    if (!startDate || !endDate) {
      return null;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Set time to start of day for accurate comparison
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const minEndTime =
      start.getTime() + DATE_VALIDATION.MILLISECONDS_PER_DAY - 1;

    if (end.getTime() <= minEndTime) {
      return { [DATE_VALIDATION.ERROR_KEY]: true };
    }

    return null;
  }

  static calculateMinEndDate(startDateValue?: string): string {
    if (startDateValue) {
      const startDate = new Date(startDateValue);
      startDate.setDate(
        startDate.getDate() + DATE_VALIDATION.MIN_DAYS_AFTER_START
      );
      return toDateInputString(startDate.toISOString());
    } else {
      const tomorrow = new Date();
      tomorrow.setDate(
        tomorrow.getDate() + DATE_VALIDATION.MIN_DAYS_AFTER_START
      );
      return toDateInputString(tomorrow.toISOString());
    }
  }

  static isEndDateValid(startDate: string, endDate: string): boolean {
    if (!startDate || !endDate) return true;

    const start = new Date(startDate);
    const end = new Date(endDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const minEndTime =
      start.getTime() + DATE_VALIDATION.MILLISECONDS_PER_DAY - 1;
    return end.getTime() > minEndTime;
  }

  static getControlValidity(
    control: AbstractControl | null | undefined
  ): boolean {
    if (!control) return false;

    // If control is disabled, temporarily enable it to check validity
    const wasDisabled = !control.enabled;
    if (wasDisabled) {
      control.enable({ emitEvent: false });
    }
    const isValid = control.valid;

    // Restore disabled state
    if (wasDisabled) {
      control.disable({ emitEvent: false });
    }

    return isValid;
  }
}
